import React, { useState, useEffect } from 'react';
import { tradingApi } from '../utils/api';
import { debugLog } from '../utils/config';

interface TradeData {
  id: string;
  type: 'buy' | 'sell';
  price: number;
  priceUsd: number;
  amount: number;
  total: number;
  totalUsd: number;
  timestamp: Date;
  txHash?: string;
  exchange: string;
  sender?: string;
  to?: string;
}

interface RecentTradesListProps {
  refreshInterval?: number;
}

const RecentTradesListComponent: React.FC<RecentTradesListProps> = ({
  refreshInterval = 30000
}) => {
  const [recentTrades, setRecentTrades] = useState<TradeData[]>([]);
  const [timeRangeUsed, setTimeRangeUsed] = useState<string>('24h');

  // Fetch recent transactions for trades list
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const to = Date.now();
        const targetCount = 100;
        let trades: TradeData[] = [];

        // Try expanding time ranges: 24h, 48h, 72h, 7d, 14d, 30d
        const timeRanges = [
          { hours: 24, label: '24h' },
          { hours: 48, label: '48h' },
          { hours: 72, label: '3d' },
          { hours: 168, label: '7d' },
          { hours: 336, label: '14d' },
          { hours: 720, label: '30d' }
        ];

        for (const range of timeRanges) {
          const from = to - (range.hours * 60 * 60 * 1000);
          debugLog(`Fetching trades from last ${range.label}...`);

          const txResponse = await tradingApi.getDexTransactionsByTimestamp(from, to, targetCount);
          debugLog(`Transactions response for ${range.label}:`, txResponse);

          let txData = txResponse;
          if (txResponse && typeof txResponse === 'object' && 'data' in txResponse) {
            txData = (txResponse as any).data;
          }

          if (txData && txData.transactions && txData.transactions.length > 0) {
            trades = txData.transactions.map((tx: any) => ({
              id: tx.id,
              type: tx.type as 'buy' | 'sell',
              price: parseFloat(tx.price || '0'),
              priceUsd: parseFloat(tx.priceUsd || '0'),
              amount: parseFloat(tx.amount || '0'),
              total: parseFloat(tx.total || '0'),
              totalUsd: parseFloat(tx.totalUsd || '0'),
              timestamp: new Date(tx.timestamp),
              txHash: tx.txHash,
              exchange: tx.exchange || 'Uniswap V2',
              sender: tx.sender,
              to: tx.to
            }));

            debugLog(`Found ${trades.length} trades in last ${range.label}`);

            // If we have enough trades or this is the last range, stop
            if (trades.length >= targetCount || range === timeRanges[timeRanges.length - 1]) {
              setTimeRangeUsed(range.label);
              break;
            }
          }
        }

        setRecentTrades(trades);
      } catch (error) {
        debugLog('Error fetching trades:', error);
        setRecentTrades([]);
      }
    };

    fetchTrades();
    const interval = setInterval(fetchTrades, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);


  const formatPrice = (price: number) => {
    if (!price || isNaN(price) || price === 0) return '-';
    if (price < 0.0001) return `$${price.toFixed(8)}`; // Very small prices (like old ETH prices)
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatAddress = (address: string | undefined) => {
    if (!address) return '-';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="border-top">
      <div className="card-body border-bottom">
        <h3 className="h6 fw-semibold mb-0">Recent Trades</h3>
      </div>
      <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
        {recentTrades.length > 0 ? (
          <table className="table table-sm small mb-0">
            <thead className="sticky-top bg-body-tertiary">
              <tr>
                <th className="text-start">Date</th>
                <th className="text-start">Type</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Price (USD)</th>
                <th className="text-end">Total (USD)</th>
                <th className="text-start">Maker</th>
                <th className="text-center">Txn</th>
              </tr>
            </thead>
            <tbody>
              {recentTrades.map((trade) => {
                const rowColor = trade.type === 'sell' ? 'text-danger' : 'text-success';
                return (
                  <tr key={trade.id} className="border-bottom">
                    <td className={`text-start ${rowColor}`} style={{ fontSize: '0.75rem' }}>
                      {new Date(trade.timestamp).toLocaleString()}
                    </td>
                    <td className={`fw-medium ${rowColor}`} style={{ fontSize: '0.75rem' }}>
                      {trade.type === 'buy' ? 'Buy' : 'Sell'}
                    </td>
                    <td className={`text-end font-monospace ${rowColor}`} style={{ fontSize: '0.75rem' }}>
                      <div>{trade.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} BWS</div>
                      <div className="opacity-75">{trade.total.toLocaleString(undefined, { maximumFractionDigits: 4 })} WETH</div>
                    </td>
                    <td className={`text-end font-monospace ${rowColor}`} style={{ fontSize: '0.75rem' }}>
                      {formatPrice(trade.priceUsd || trade.price)}
                    </td>
                    <td className={`text-end font-monospace ${rowColor}`} style={{ fontSize: '0.75rem' }}>
                      ${(trade.totalUsd || (trade.total * (trade.priceUsd || trade.price))).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className={rowColor}>
                      <span className="font-monospace" style={{ fontSize: '0.75rem' }}>{formatAddress(trade.sender || trade.to)}</span>
                    </td>
                    <td className={`text-center ${rowColor}`}>
                      {trade.txHash && (
                        <a
                          href={`https://etherscan.io/tx/${trade.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-decoration-none ${rowColor}`}
                          style={{ fontSize: '0.75rem' }}
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="p-5 text-center">
            <i className="bi bi-activity fs-2 text-muted d-block mb-2"></i>
            <p className="small text-muted mb-1">No transactions in the last 24 hours</p>
            <p className="small text-body-secondary mb-0">Trades will appear here once BWS trading activity is detected</p>
          </div>
        )}
      </div>
      <div className="card-footer bg-body-tertiary text-center">
        <p className="small text-muted mb-0">
          Showing last {recentTrades.length} BWS/ETH swaps from Uniswap V2 (last {timeRangeUsed}) • Updated every {refreshInterval / 1000}s
        </p>
      </div>
    </div>
  );
};

RecentTradesListComponent.displayName = 'RecentTradesList';

export const RecentTradesList = React.memo(RecentTradesListComponent);
